import React, { Component, Fragment } from "react";
import { Button, FormGroup, Label, Input, Row, Col } from "reactstrap";
import DataTable from "react-data-table-component";
import Loader from "react-loaders";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import SweetAlert from "react-bootstrap-sweetalert";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../Components/Toaster";
import axiosInstance from "../configuration/Request";
import { formatDate } from "../../utils/dateForamt";
import "react-toastify/dist/ReactToastify.css";
import { RiDeleteBin6Line } from "react-icons/ri";
import TooltipComponent from "../ReusableComponent/TooltipComponent";
import Delete from "../../assets/images/svg/Delete.svg";
import Edit from "../../assets/images/svg/Edit.svg";
import View from "../../assets/images/svg/View.svg";

class PopulationProjection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      projectionData: [],
      loading: true,
      deleting: false,
      total: 0,
      clearSelectedRows: false,
      resetPagination: false,
      selectedRows: [],
      openDeletePopup: false,
      openEditPopup: false,
      selectedRowForEdit: null,
      selectedRowsForDelete: [],
      editName: "",
      editNameError: "",
      page: 1,
      pageSize: 10,
      now: Date.now(),
    };
  }

  componentDidMount() {
    this.fetchPopulationProjections();
    this.expiryTimer = setInterval(() => {
      this.setState({ now: Date.now() }, this.checkAndPollStatus);
    }, 5000);
  }

  componentWillUnmount() {
    clearInterval(this.pollingTimer);
    clearInterval(this.expiryTimer);
    this.requestController?.abort();
    this.deleteController?.abort();
  }

  checkAndPollStatus = () => {
    const needsFastPolling = this.state.projectionData.some((row) => {
      const remaining = Date.parse(row.expiresAt) - Date.now();
      return row.status.trim().toLowerCase() === "running" ||
        (remaining > 0 && remaining < 5 * 60 * 1000);
    });
    const hasDone = this.state.projectionData.some(
      (row) => row.status.trim().toLowerCase() === "done"
    );
    const interval = needsFastPolling ? 5000 : hasDone ? 5 * 60 * 1000 : null;
    if (this.pollingTimer && this.pollingInterval === interval) return;

    clearInterval(this.pollingTimer);
    this.pollingTimer = null;
    this.pollingInterval = interval;
    if (interval) {
      this.pollingTimer = setInterval(() => this.fetchPopulationProjections(true), interval);
    }
  };

  fetchPopulationProjections = async (isPolling = false) => {
    if (isPolling && (this.requestController || this.state.deleting)) return;
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    const { page, pageSize } = this.state;
    if (!isPolling) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
      this.setState((state) => ({
        loading: true,
        selectedRows: [],
        clearSelectedRows: !state.clearSelectedRows,
      }));
    }

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}api/population-projections`,
        {
          params: {
            "pagination[page]": page,
            "pagination[pageSize]": pageSize,
          },
          signal: controller.signal,
        }
      );
      if (controller.signal.aborted) return;

      const projectionData = response.data.data.map((item) => {
        const attributes = item.attributes || {};
        return {
          id: item.id,
          analysisId: attributes.analysis_id,
          metadata: attributes,
          status: attributes.status || "-",
          taskName: attributes.task_name || "-",
          submitTime: attributes.createdAt ? formatDate(attributes.createdAt) : "-",
          expiresAt: attributes.expires_at,
        };
      });

      this.setState({
        projectionData,
        total: response.data.meta?.pagination?.total ?? projectionData.length,
        loading: false,
        now: Date.now(),
      }, this.checkAndPollStatus);
    } catch (error) {
      if (controller.signal.aborted) return;
      if (isPolling) return;
      const message = "Error while fetching population projections";
      this.setState({ projectionData: [], total: 0, loading: false });
      showErrorToast(message);
    } finally {
      if (this.requestController === controller) this.requestController = null;
    }
  };

  handleNewProjection = () => {
    this.props.history.push("/app/populationProjection/new");
  };

  getAvailabilityLabel = (row) => {
    const expires = Date.parse(row.expiresAt);
    if (!Number.isFinite(expires)) return "-";
    const minutes = Math.ceil((expires - this.state.now) / 60000);
    return minutes <= 0 ? "Expired" : `${Math.floor(minutes / 60)}h ${minutes % 60}m remaining`;
  };

  getViewDisabledReason = (row) => {
    const status = row.status.trim().toLowerCase();
    if (status === "failed") return "View Expired";
    if (status === "expired" || Date.parse(row.expiresAt) <= Date.now()) {
      return "View unavailable: projection expired";
    }
    return "";
  };

  handleView = (row) => {
    if (this.getViewDisabledReason(row)) return;
    if (!row.analysisId) {
      showErrorToast("Analysis ID is missing for this projection.");
      return;
    }
    this.props.history.push({
      pathname: `/app/populationProjection/view/${encodeURIComponent(row.analysisId)}`,
      state: { projection: row.metadata },
    });
  };

  handleEditEvent = (row) => {
    this.setState({
      selectedRowForEdit: row,
      editName: row.taskName,
      editNameError: "",
      openEditPopup: true,
    });
  };

  handleDeleteEvent = (row) => {
    if (this.state.deleting) return;
    this.setState({ selectedRowsForDelete: [row.id], openDeletePopup: true });
  };

  handleMultiDelete = () => {
    if (this.state.deleting || !this.state.selectedRows.length) return;
    const ids = this.state.selectedRows.map((r) => r.id);
    this.setState({ selectedRowsForDelete: ids, openDeletePopup: true });
  };

  confirmDelete = async () => {
    const { selectedRowsForDelete } = this.state;
    if (this.deleteController || !selectedRowsForDelete.length) return;

    const controller = new AbortController();
    this.deleteController = controller;
    this.setState({ deleting: true });

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}api/projection-custom/delete_projection`,
        { ids: selectedRowsForDelete },
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;

      showSuccessToast(
        selectedRowsForDelete.length === 1
          ? "Projection deleted successfully"
          : "Projections deleted successfully"
      );
      this.setState(
        (state) => ({
          deleting: false,
          openDeletePopup: false,
          selectedRows: [],
          selectedRowsForDelete: [],
          projectionData: [],
          total: 0,
          page: 1,
          resetPagination: !state.resetPagination,
        }),
        this.fetchPopulationProjections
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      this.setState({ deleting: false });
      showErrorToast("Error while deleting population projections. Please try again.");
    } finally {
      if (this.deleteController === controller) this.deleteController = null;
    }
  };

  confirmEdit = () => {
    const { editName, selectedRowForEdit, projectionData } = this.state;
    if (!editName.trim()) {
      this.setState({ editNameError: "Name cannot be empty." });
      return;
    }
    const updated = projectionData.map((r) =>
      r.id === selectedRowForEdit.id ? { ...r, taskName: editName.trim() } : r
    );
    this.setState({
      projectionData: updated,
      openEditPopup: false,
      selectedRowForEdit: null,
    });
    showSuccessToast("Task name updated successfully");
  };

  closePopup = () => {
    if (this.state.deleting) return;
    this.setState({
      openDeletePopup: false,
      openEditPopup: false,
      selectedRowForEdit: null,
      selectedRowsForDelete: [],
      editName: "",
      editNameError: "",
    });
  };

  rowSelectionHandler = ({ selectedRows }) => {
    this.setState({ selectedRows });
  };

  handlePageChange = (page) => {
    if (page === this.state.page) return;
    this.setState({ page }, this.fetchPopulationProjections);
  };

  handleRowsPerPageChange = (newPerPage) => {
    if (newPerPage === this.state.pageSize) return;
    this.setState(
      (state) => ({
        pageSize: newPerPage,
        page: 1,
        resetPagination: !state.resetPagination,
      }),
      this.fetchPopulationProjections
    );
  };

  render() {
    const {
      projectionData,
      selectedRows,
      openDeletePopup,
      openEditPopup,
      editName,
      editNameError,
    } = this.state;

    const columns = [
      {
        name: <span className="table-header">Status</span>,
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <div className={`status-badge ${row.status.toLowerCase().replace(/\s+/g, "-")}`}>
            <span>{row.status}</span>
          </div>
        ),
        width: "150px",
      },
      {
        name: <span className="table-header">Projection Task List</span>,
        selector: (row) => row.taskName,
        cell: (row) => (
          <div className="network-name-container">
            <span>{row.taskName}</span>
          </div>
        ),
        sortable: true,
      },
      {
        name: <span className="table-header">Submit Time</span>,
        selector: (row) => row.submitTime,
        sortable: true,
        width: "180px",
      },
      {
        name: <span className="table-header">Availability</span>,
        selector: (row) => this.getAvailabilityLabel(row),
        width: "180px",
      },
      {
        name: <span className="table-header">Actions</span>,
        cell: (row) => (
          <div className="button-group-table">
            <TooltipComponent
              id={`tooltip-eye-${row.id}`}
              tooltipText={this.getViewDisabledReason(row) || "View"}
            >
              <img
                className="image-icon"
                src={View}
                alt="View projection"
                role="button"
                tabIndex={this.getViewDisabledReason(row) ? -1 : 0}
                aria-disabled={Boolean(this.getViewDisabledReason(row))}
                style={{
                  cursor: this.getViewDisabledReason(row) ? "not-allowed" : "pointer",
                  opacity: this.getViewDisabledReason(row) ? 0.4 : 1,
                }}
                onClick={() => this.handleView(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    this.handleView(row);
                  }
                }}
              />
            </TooltipComponent>
            <TooltipComponent
              id={`tooltip-edit-${row.id}`}
              tooltipText="Edit"
            >
              <img
                className="image-icon"
                src={Edit}
                alt=""
                style={{ cursor: "pointer" }}
                onClick={() => this.handleEditEvent(row)}
              />
            </TooltipComponent>
            <TooltipComponent
              id={`tooltip-delete-${row.id}`}
              tooltipText="Delete"
            >
              <img
                className="image-icon"
                src={Delete}
                alt=""
                style={{ cursor: "pointer" }}
                onClick={() => this.handleDeleteEvent(row)}
              />
            </TooltipComponent>
          </div>
        ),
        width: "180px",
      },
    ];

    return (
      <div className="network-container-outer">
        <div className="network-container">
          <ToastContainer />

          <div className="top-div flex-wrap">
            <div className="title-div fw-bold">Population Projection</div>
            <div className="flex-wrap">
              <Button
                className="create-button-parameter me-2"
                color="primary"
                onClick={this.handleNewProjection}
              >
                New Projection
              </Button>
            </div>
          </div>

          <div className="network-button-container">
            <Button
              className="delete-button-div"
              color="danger"
              onClick={this.handleMultiDelete}
              disabled={selectedRows.length <= 0 || this.state.deleting}
              aria-label="Delete selected projections"
            >
              <RiDeleteBin6Line size={18} />
            </Button>
          </div>

          <div className="error-message-operation" />

          <div className="network-table">
            <Fragment>
              <TransitionGroup>
                <CSSTransition
                  component="div"
                  classNames="TabsAnimation"
                  appear={true}
                  timeout={1500}
                  enter={false}
                  exit={false}
                >
                  <div>
                    <Row>
                      <Col md="12">
                        <DataTable
                          columns={columns}
                          data={projectionData}
                          noDataComponent="There are no records to display"
                          selectableRows
                          selectableRowDisabled={(row) =>
                            !["done", "failed", "expired"].includes(row.status.trim().toLowerCase())
                          }
                          clearSelectedRows={this.state.clearSelectedRows}
                          onSelectedRowsChange={this.rowSelectionHandler}
                          pagination
                          paginationServer
                          paginationTotalRows={this.state.total}
                          paginationPerPage={this.state.pageSize}
                          paginationDefaultPage={1}
                          paginationResetDefaultPage={this.state.resetPagination}
                          paginationRowsPerPageOptions={[10, 50, 100]}
                          onChangePage={this.handlePageChange}
                          onChangeRowsPerPage={this.handleRowsPerPageChange}
                        />
                      </Col>
                    </Row>
                  </div>
                </CSSTransition>
              </TransitionGroup>
            </Fragment>
          </div>

          {this.state.loading && (
            <div className="backdrop" role="status" aria-label="Loading population projections">
              <div className="loader">
                <Loader type="ball-pulse-rise" />
              </div>
            </div>
          )}

          {/* Edit Popup */}
          <SweetAlert
            custom
            show={openEditPopup}
            customButtons={<React.Fragment></React.Fragment>}
            style={{ width: "auto", padding: "1rem" }}
          >
            <div className="edit-popup">
              <div className="popup-header">Edit Name</div>
              <div className="button-popup-container">
                <div className="my-2">
                  <FormGroup floating className="ms-1">
                    <Input
                      type="text"
                      id="editTaskName"
                      placeholder="Edit Task Name"
                      style={{ height: "58px" }}
                      value={editName}
                      onChange={(e) =>
                        this.setState({
                          editName: e.target.value,
                          editNameError: "",
                        })
                      }
                    />
                    <Label for="editTaskName">Edit Name</Label>
                    <div className="error-message-edit">
                      {editNameError && (
                        <div className="text-danger mt-1">{editNameError}</div>
                      )}
                    </div>
                  </FormGroup>
                </div>
                <button
                  className="btn btn-lg btn-light btn-cancel"
                  onClick={this.closePopup}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-lg btn-primary btn-delete"
                  onClick={this.confirmEdit}
                >
                  Save
                </button>
              </div>
            </div>
          </SweetAlert>

          {/* Delete Popup */}
          <SweetAlert
            custom
            show={openDeletePopup}
            customButtons={<React.Fragment></React.Fragment>}
            style={{ width: "auto", padding: "1rem" }}
          >
            <div className="delete-project-container">
              <div className="popup-header">
                {this.state.selectedRowsForDelete.length > 1
                  ? "Are you sure you want to delete these projections?"
                  : "Are you sure you want to delete this projection?"}
              </div>
              <div className="button-popup-container">
                <button
                  className="btn btn-lg btn-light btn-cancel"
                  onClick={this.closePopup}
                  disabled={this.state.deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-lg btn-primary btn-delete"
                  onClick={this.confirmDelete}
                  disabled={this.state.deleting}
                >
                  {this.state.deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </SweetAlert>
        </div>
      </div>
    );
  }
}

export default PopulationProjection;
